import React from 'react';
import {
  formatUnits as formatStock,
  formatArrivalDate,
  hasColorBreakdown,
  arrivalsByColor,
  findColorArrivals,
} from '../utils/arrivals';
import colorSwatch from '../utils/colorSwatch';
import fixEncoding from '../utils/fixEncoding';
import '../styles/components/ProductVariationsTable.scss';

/**
 * @file ProductVariationsTable.jsx
 * @description A component that displays a table of stock availability for product variations.
 * It intelligently handles different data structures for variations based on the provider.
 * Si el producto tiene próximas llegadas por color (`arrivals`), añade las columnas
 * "En camino" y "Llegada estimada".
 */

/** Nombre del color con su muestra; sin muestra si el nombre no trae un color ("Default Title"). */
const ColorCell = ({ name }) => {
  const label = fixEncoding(String(name ?? ''));
  const swatch = colorSwatch(label);
  return (
    <td>
      <span className="ProductVariationsTable__color">
        {swatch ? (
          <span
            className={`ProductVariationsTable__swatch${swatch.light ? ' ProductVariationsTable__swatch--light' : ''}`}
            style={{ background: swatch.background }}
            aria-hidden="true"
          />
        ) : (
          <span className="ProductVariationsTable__swatch ProductVariationsTable__swatch--empty" aria-hidden="true" />
        )}
        {label}
      </span>
    </td>
  );
};

const toNumber = (value) => (Number.isFinite(Number(value)) ? Number(value) : 0);

/** Celdas "En camino" y "Llegada estimada" de un color. */
const ArrivalCells = ({ incoming }) => {
  if (!incoming) {
    return (
      <>
        <td className="ProductVariationsTable__none">—</td>
        <td className="ProductVariationsTable__none">—</td>
      </>
    );
  }
  const multiple = incoming.envios.length > 1;
  return (
    <>
      <td className="ProductVariationsTable__incoming">+{formatStock(incoming.total)}</td>
      <td className="ProductVariationsTable__eta">
        {multiple ? (
          <ul>
            {incoming.envios.map((envio) => (
              <li key={envio.fecha}>
                {formatArrivalDate(envio.fecha)} <span>· {formatStock(envio.cantidad)}</span>
              </li>
            ))}
          </ul>
        ) : formatArrivalDate(incoming.envios[0].fecha)}
      </td>
    </>
  );
};

const ProductVariationsTable = ({ variations, warehouse, arrivals = [] }) => {

  // --- Caso Principal: Para bodegas con variaciones en formato de objeto simple ---
  // Esto funcionará para CDO, PromoOpcion, Catalogos, y cualquier proveedor que
  // envíe el stock en el formato: { "Color/Variante": Cantidad }
  if (variations && typeof variations === 'object' && !Array.isArray(variations)) {
    const variationEntries = Object.entries(variations);
    const showArrivals = hasColorBreakdown(arrivals);
    const byColor = showArrivals ? arrivalsByColor(arrivals) : new Map();

    // Colores que solo existen en lo que viene en camino (sin fila de existencias).
    const listed = new Set(variationEntries.map(([key]) => key.trim().toLowerCase()));
    const incomingOnly = [...byColor.entries()].filter(([key]) => !listed.has(key));

    if (variationEntries.length === 0 && incomingOnly.length === 0) {
      return (
        <div className="ProductVariationsTable">
          <div className="ProductVariationsTable__header">
            <h3 className="ProductVariationsTable__title">Existencias</h3>
          </div>
          <p className="empty_inventory">No hay información de stock disponible para este producto.</p>
        </div>
      );
    }
    
    const totalStock = variationEntries.reduce((sum, [, value]) => sum + toNumber(value), 0);
    const totalIncoming = [...byColor.values()].reduce((sum, entry) => sum + entry.total, 0);
    const rowCount = variationEntries.length + incomingOnly.length;

    // Renderiza la tabla con los datos reales de stock.
    return (
      <div className="ProductVariationsTable">
        <div className="ProductVariationsTable__header">
          <h3 className="ProductVariationsTable__title">Existencias En Stock</h3>
          <p className="ProductVariationsTable__subtitle">
            {rowCount} {rowCount === 1 ? 'variación' : 'variaciones'} · {formatStock(totalStock)} unidades en stock
          </p>
        </div>
        <div className="ProductVariationsTable__scroll">
          <table className="ProductVariationsTable__table">
            <thead>
              <tr>
                <th>Color / Variación</th>
                <th>En Stock</th>
                {showArrivals && <th>En camino</th>}
                {showArrivals && <th>Llegada estimada</th>}
              </tr>
            </thead>
            <tbody>
              {variationEntries.map(([key, value]) => (
                <tr key={key}>
                  <ColorCell name={key} />
                  <td className={toNumber(value) === 0 ? 'ProductVariationsTable__zero' : undefined}>{formatStock(value)}</td>
                  {showArrivals && <ArrivalCells incoming={findColorArrivals(byColor, key)} />}
                </tr>
              ))}
              {incomingOnly.map(([key, incoming]) => (
                <tr key={`incoming-${key}`}>
                  <ColorCell name={incoming.color} />
                  <td className="ProductVariationsTable__zero">0</td>
                  <ArrivalCells incoming={incoming} />
                </tr>
              ))}
            </tbody>
            {rowCount > 1 && (
              <tfoot>
                <tr>
                  <td>Totales</td>
                  <td>{formatStock(totalStock)}</td>
                  {showArrivals && <td className="ProductVariationsTable__incoming">+{formatStock(totalIncoming)}</td>}
                  {showArrivals && <td />}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {showArrivals && (
          <p className="ProductVariationsTable__note">
            Las fechas de llegada son estimadas por la bodega y pueden variar.
          </p>
        )}
      </div>
    );
  }

  // --- Caso Específico: Para bodegas como Marpico que usan un array de objetos ---
  // Mantenemos esta lógica para cuando construyamos el adaptador de Marpico.
  if (warehouse === 'Marpico' && Array.isArray(variations)) {
    return (
      <div className="ProductVariationsTable">
        <div className="ProductVariationsTable__header">
          <h3 className="ProductVariationsTable__title">Existencias En Stock</h3>
        </div>
        <div className="ProductVariationsTable__scroll">
          <table className="ProductVariationsTable__table">
            <thead>
              <tr>
                <th>Variedad</th>
                <th>Código</th>
                <th>Disponibles</th>
                {/* Puedes añadir más columnas específicas para Marpico aquí */}
              </tr>
            </thead>
            <tbody>
              {variations.map((variation, index) => (
                <tr key={variation.Codigo || index}>
                  <ColorCell name={variation.Variedad} />
                  <td>{variation.Codigo}</td>
                  <td>{formatStock(variation['Disponibles en inventario'] || variation['Disponibles en Inventario'])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // --- Caso por Defecto (Fallback) ---
  // Se muestra si 'variations' es nulo, indefinido o tiene un formato no reconocido.
  return (
    <div className="ProductVariationsTable">
      <div className="ProductVariationsTable__header">
        <h3 className="ProductVariationsTable__title">Existencias</h3>
      </div>
      <p className="empty_inventory">Información de stock no disponible o en formato no reconocido.</p>
    </div>
  );
};

export default ProductVariationsTable;